import React, { useRef, useState } from 'react';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon, PaperclipIcon, SendIcon, XIcon } from 'lucide-react';
import { FUNDING_RANGES, PURPOSES, INDUSTRIES } from '../../data/content';
import { useLanguage } from '../../i18n/LanguageContext';
type FormState = {
  name: string;
  company: string;
  country: string;
  email: string;
  telephone: string;
  funding: string;
  purpose: string;
  industry: string;
  message: string;
};
const EMPTY: FormState = {
  name: '',
  company: '',
  country: '',
  email: '',
  telephone: '',
  funding: '',
  purpose: '',
  industry: '',
  message: ''
};
type Status = 'idle' | 'submitting' | 'success' | 'error';
const inputClass = 'w-full rounded-xl border border-navy-100 bg-white px-4 py-3 text-sm text-charcoal placeholder:text-charcoal-light/50 transition-colors focus:border-gold focus:outline-none';
const labelClass = 'block text-[11px] font-semibold uppercase tracking-[0.14em] text-navy';
export function EnquiryForm() {
  const {
    lang,
    p
  } = useLanguage();
  const f = p.contact.form;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [files, setFiles] = useState<string[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const fileInput = useRef<HTMLInputElement>(null);
  const update = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [key]: e.target.value
    }));
    setErrors(prev => ({
      ...prev,
      [key]: undefined
    }));
  };
  const validate = () => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) next.name = f.errors.name;
    if (!form.country.trim()) next.country = f.errors.country;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = f.errors.email;
    if (!form.funding) next.funding = f.errors.funding;
    if (!form.message.trim() || form.message.trim().length < 20) next.message = f.errors.message;
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    await new Promise(r => setTimeout(r, 1200));
    setStatus('success');
    setForm(EMPTY);
    setFiles([]);
  };
  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = Array.from(e.target.files ?? []).map(file => file.name);
    setFiles(prev => [...prev, ...list].slice(0, 5));
  };
  if (status === 'success') {
    return <div role="status" className="rounded-3xl border border-navy-100 bg-white p-10 text-center shadow-soft" data-id="element-21" data-mp="3d0mv7o0cpjlv">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-navy text-gold" data-id="element-22" data-mp="3351oxdo91x6b">
          <CheckCircle2Icon className="h-6 w-6" aria-hidden="true" data-id="element-23" data-mp="2z49gunr45ybm" />
        </span>
        <h3 className="mt-6 font-display text-2xl font-medium text-navy" data-id="element-24" data-mp="1elp8h9see55a">{f.successTitle}</h3>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-charcoal-light" data-id="element-25" data-mp="1im7z50ik155w">
          {f.successText}
        </p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-8 inline-flex items-center gap-2 rounded-full border border-navy-100 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:border-gold hover:text-gold-dark" data-id="element-26" data-mp="1nn5hpe7h817x">
          {f.successAgain}
        </button>
      </div>;
  }
  return <form onSubmit={handleSubmit} noValidate className="rounded-3xl border border-navy-100 bg-white p-7 shadow-soft sm:p-10" data-id="element-27" data-mp="14ub9r489t9d7">
      <h2 className="font-display text-2xl font-medium text-navy sm:text-3xl" data-id="element-28" data-mp="19nvxm3g5gmyo">{f.title}</h2>
      <p className="mt-3 text-sm leading-relaxed text-charcoal-light" data-id="element-29" data-mp="c4umekzicinu">{f.intro}</p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-30" data-mp="h3xj9i5c3ikq">
        <div data-id="element-31" data-mp="2iu63eb1iwcy9">
          <label htmlFor="name" className={labelClass} data-id="element-32" data-mp="2ivz7bikr4j2k">
            {f.name}
          </label>
          <input id="name" className={`${inputClass} mt-2`} value={form.name} onChange={update('name')} aria-invalid={!!errors.name} placeholder={f.namePlaceholder} data-id="element-33" data-mp="2eudozhpg7ckz" />
          {errors.name && <FieldError message={errors.name} data-id="element-34" data-mp="32iol67k78o2x" />}
        </div>

        <div data-id="element-35" data-mp="32h3r0j1sr75l">
          <label htmlFor="company" className={labelClass} data-id="element-36" data-mp="32l18z1cn1snw">
            {f.company}
          </label>
          <input id="company" className={`${inputClass} mt-2`} value={form.company} onChange={update('company')} placeholder={f.companyPlaceholder} data-id="element-37" data-mp="2xm6j5fg973ua" />
        </div>

        <div data-id="element-38" data-mp="1h52b4w0tnyb3">
          <label htmlFor="country" className={labelClass} data-id="element-39" data-mp="1h6n62mr1tj9h">
            {f.country}
          </label>
          <input id="country" className={`${inputClass} mt-2`} value={form.country} onChange={update('country')} aria-invalid={!!errors.country} placeholder={f.countryPlaceholder} data-id="element-40" data-mp="1c7k9lqp4wld4" />
          {errors.country && <FieldError message={errors.country} data-id="element-41" data-mp="3f229vnhvnbgm" />}
        </div>

        <div data-id="element-42" data-mp="3ezpwlnn2dp9w">
          <label htmlFor="email" className={labelClass} data-id="element-43" data-mp="3j34b29vctxl8">
            {f.email}
          </label>
          <input id="email" type="email" className={`${inputClass} mt-2`} value={form.email} onChange={update('email')} aria-invalid={!!errors.email} placeholder={f.emailPlaceholder} data-id="element-44" data-mp="353cuc1dge33j" />
          {errors.email && <FieldError message={errors.email} data-id="element-45" data-mp="304a0ld2xbjav" />}
        </div>

        <div data-id="element-46" data-mp="304s1in0j6lp4">
          <label htmlFor="telephone" className={labelClass} data-id="element-47" data-mp="306cn3n4vauau">
            {f.phone}
          </label>
          <input id="telephone" type="tel" className={`${inputClass} mt-2`} value={form.telephone} onChange={update('telephone')} placeholder={f.phonePlaceholder} data-id="element-48" data-mp="u978a1w0ozvd" />
        </div>

        <div data-id="element-49" data-mp="229pf66sp3sdf">
          <label htmlFor="funding" className={labelClass} data-id="element-50" data-mp="2297d2tjcqjxl">
            {f.funding}
          </label>
          <select id="funding" className={`${inputClass} mt-2`} value={form.funding} onChange={update('funding')} aria-invalid={!!errors.funding} data-id="element-51" data-mp="1y9p1wphc1n7y">
            <option value="" data-id="element-52" data-mp="2q2fp6ah1g1l2">{f.fundingPlaceholder}</option>
            {FUNDING_RANGES.map(range => <option key={range.en} value={range[lang]} data-id="element-53" data-mp="2m13ye1mfhaba">
                {range[lang]}
              </option>)}
          </select>
          {errors.funding && <FieldError message={errors.funding} data-id="element-54" data-mp="2c85a6kuufls7" />}
        </div>

        <div data-id="element-55" data-mp="39ou7q2crg1vs">
          <label htmlFor="purpose" className={labelClass} data-id="element-56" data-mp="39okem5wf5elv">
            {f.purpose}
          </label>
          <select id="purpose" className={`${inputClass} mt-2`} value={form.purpose} onChange={update('purpose')} data-id="element-57" data-mp="34xc9hm24gfei">
            <option value="" data-id="element-58" data-mp="3h7lbi8cpsa09">{f.purposePlaceholder}</option>
            {PURPOSES.map(purpose => <option key={purpose.en} value={purpose[lang]} data-id="element-59" data-mp="3m76equyx34is">
                {purpose[lang]}
              </option>)}
          </select>
        </div>

        <div data-id="element-60" data-mp="3q6f3tmj0ie80">
          <label htmlFor="industry" className={labelClass} data-id="element-61" data-mp="3q66uu5q0ft23">
            {f.industry}
          </label>
          <select id="industry" className={`${inputClass} mt-2`} value={form.industry} onChange={update('industry')} data-id="element-62" data-mp="41bj4du7nkyy">
            <option value="" data-id="element-63" data-mp="8u4moydy0n9g">{f.industryPlaceholder}</option>
            {INDUSTRIES.map(industry => <option key={industry.name.en} value={industry.name[lang]} data-id="element-64" data-mp="dsxq1xzimxvw">
                {industry.name[lang]}
              </option>)}
            <option value={f.other} data-id="element-65" data-mp="msukkg8l6qyv">{f.other}</option>
          </select>
        </div>

        <div className="sm:col-span-2" data-id="element-66" data-mp="18286ig5dnmdk">
          <label htmlFor="message" className={labelClass} data-id="element-67" data-mp="1816nnpi4dvla">
            {f.message}
          </label>
          <textarea id="message" rows={5} className={`${inputClass} mt-2 resize-y`} value={form.message} onChange={update('message')} aria-invalid={!!errors.message} placeholder={f.messagePlaceholder} data-id="element-68" data-mp="133etspxwzzeg" />
          {errors.message && <FieldError message={errors.message} data-id="element-69" data-mp="pa5l6rhsjhwz" />}
        </div>

        <div className="sm:col-span-2" data-id="element-70" data-mp="kehxyhmvcyu4">
          <span className={labelClass} data-id="element-71" data-mp="kbcaf6tw250i">{f.upload}</span>
          <button type="button" onClick={() => fileInput.current?.click()} className="mt-2 flex w-full items-center justify-center gap-3 rounded-xl border border-dashed border-navy-100 bg-mist px-4 py-6 text-center text-sm text-charcoal-light transition-colors hover:border-gold hover:text-navy" data-id="element-72" data-mp="z2uylrwwj5uy">
            <PaperclipIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-73" data-mp="1kuslxalgttx" />
            {f.uploadHint}
          </button>
          <input ref={fileInput} id="documents" type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" onChange={handleFiles} className="sr-only" data-id="element-74" data-mp="2i19bgrdd574o" />
          {files.length > 0 && <ul className="mt-3 flex flex-wrap gap-2" data-id="element-75" data-mp="2w0s7wmwtau15">
              {files.map((file, idx) => <li key={`${file}-${idx}`} className="flex items-center gap-2 rounded-full bg-navy-50 px-4 py-2 text-xs text-navy" data-id="element-76" data-mp="30zd9oqrpplrp" data-mpi={idx}>
                  {file}
                  <button type="button" aria-label={`${f.remove} ${file}`} onClick={() => setFiles(prev => prev.filter((_, j) => j !== idx))} className="text-charcoal-light transition-colors hover:text-gold-dark" data-id="element-77" data-mp="2r0wcvd63ecxq">
                    <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-78" data-mp="3unqnqnmwc00" />
                  </button>
                </li>)}
            </ul>}
        </div>
      </div>

      {status === 'error' && <p role="alert" className="mt-6 flex items-start gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-79" data-mp="3r5x3yi9xjxh5">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-80" data-mp="3h8iretr4nsu4" />
          {f.errorBanner}
        </p>}

      <button type="submit" disabled={status === 'submitting'} className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.16em] text-white transition-all duration-300 hover:bg-navy-700 hover:shadow-lift disabled:opacity-60 sm:w-auto" data-id="element-81" data-mp="3m7vmg1s5k67s">
        {status === 'submitting' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-82" data-mp="1agrqxm4qw3gx" />
            {f.submitting}
          </> : <>
            {f.submit}
            <SendIcon className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" data-id="element-83" data-mp="2qz1tbnfo568e" />
          </>}
      </button>

      <p className="mt-5 text-xs leading-relaxed text-charcoal-light" data-id="element-84" data-mp="2zyybd67jz6uh">{f.consent}</p>
    </form>;
}
function FieldError({
  message
}: {
  message: string;
}) {
  return <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600" data-id="element-85" data-mp="39p00807qctag">
      <AlertCircleIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-86" data-mp="39oq743re260j" />
      {message}
    </p>;
}